/**
 * What each person may do in the customer portal, and how their credentials
 * are handed over.
 *
 * Shared by the two screens that mint credentials - issuing a licence and
 * claiming a spare seat on one - so the role vocabulary and the copied text
 * cannot drift apart between them. Kept out of either component file because
 * a file exporting both components and helpers loses fast refresh.
 */

/**
 * Owner is the account's administrator - they manage the seat roster - so an
 * organization wants exactly one. Which role is the DEFAULT is decided by
 * each form, not by the order here.
 */
export const ROLES = [
  { value: 'owner', label: 'Owner' },
  { value: 'technician', label: 'Technician' },
  { value: 'viewer', label: 'Viewer' },
] as const;

export type SeatRole = (typeof ROLES)[number]['value'];

export const roleLabel = (role: string): string =>
  ROLES.find((r) => r.value === role)?.label ?? role;

/**
 * The roles a seat added *after* a licence's initial creation may take.
 *
 * Ownership is decided once, the moment an organization's first seats are
 * created (`IssueLicenseDialog`) - never here. The server refuses `owner` on
 * this path outright (`OwnerAlreadyAssignedError`), and this list is why the
 * picker never offers it in the first place.
 */
export const ADDABLE_ROLES = ROLES.filter((role) => role.value !== 'owner');

export type AddableSeatRole = Exclude<SeatRole, 'owner'>;

/**
 * Everything on a results screen, as one block of text.
 *
 * One "Copy all" rather than a copy button per value: what an admin does with
 * that screen is hand the whole thing over - pasted into an email to the lab,
 * or into wherever they keep handover notes - and doing that four icon-clicks
 * at a time, alternating with pastes, is how a password ends up next to the
 * wrong person's name.
 *
 * Labelled lines rather than tab-separated columns, because the destination is
 * a message to a human, not a spreadsheet. The values stay selectable on
 * screen, so copying just one by hand is still possible.
 */
export function credentialsText(
  seats: {
    fullName: string;
    email: string;
    role: string;
    licenseKey: string;
    password?: string;
  }[],
): string {
  return seats
    .map((seat) => {
      const lines = [
        `${seat.fullName} (${roleLabel(seat.role)})`,
        `Email: ${seat.email}`,
        `Licence key: ${seat.licenseKey}`,
      ];
      // Absent when the admin supplied one: it is not echoed back, so there is
      // nothing to copy and a "Password:" line with nothing after it would
      // read as a bug.
      if (seat.password !== undefined) lines.push(`Password: ${seat.password}`);
      return lines.join('\n');
    })
    .join('\n\n');
}
